import React, { useState, useEffect } from 'react';
import { Container, Nav, Card, Badge, Row, Col, InputGroup } from 'react-bootstrap';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Authentication from './components/Authentication';
import DataView from './components/DataView';
import DataAnalysis from './components/DataAnalysis';
import Questionnaire from './components/Questionnaire';
import Reporting from './components/Reporting';
import DataAugmentation from './components/DataAugmentation';
import Header from './components/Header';
import EmbeddedApp from './components/EmbeddedApp';
import { config } from './config/env';
import { 
  updateOrganization, 
  ORG_ID_CHANGE_EVENT,
  clearCache
} from './services/api';
import './App.css';

// Read feature flags from window.ENV (runtime) or process.env (build time)
const SHOW_DATA_VIEW = (window.ENV?.SHOW_DATA_VIEW ?? process.env.REACT_APP_SHOW_DATA_VIEW ?? 'true') === 'true';
const SHOW_DATA_ANALYSIS = (window.ENV?.SHOW_DATA_ANALYSIS ?? process.env.REACT_APP_SHOW_DATA_ANALYSIS ?? 'true') === 'true';
const SHOW_DATA_AUGMENTATION = (window.ENV?.SHOW_DATA_AUGMENTATION ?? process.env.REACT_APP_SHOW_DATA_AUGMENTATION ?? 'true') === 'true';

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/auth" />;
  }
  
  return children;
};

const Navigation = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  if (!isAuthenticated) return null;

  const getActiveClass = (path) => {
    return window.location.pathname === path ? 'active' : '';
  };

  return (
    <Card className="mb-4 shadow-sm">
      <Card.Body className="p-0">
        <Nav variant="tabs" className="border-0" style={{ backgroundColor: '#f8f9fa' }}>
          {SHOW_DATA_VIEW && (
            <Nav.Item>
              <Nav.Link 
                onClick={() => navigate('/')}
                className={getActiveClass('/')}
                style={{ 
                  borderBottom: window.location.pathname === '/' ? '3px solid #00A651' : 'none',
                  fontWeight: window.location.pathname === '/' ? '600' : '400',
                  color: window.location.pathname === '/' ? '#00A651' : '#495057'
                }}
              >
                <i className="bi bi-table me-2"></i>
                Data View
              </Nav.Link>
            </Nav.Item>
          )}
          {SHOW_DATA_ANALYSIS && (
            <Nav.Item>
              <Nav.Link 
                onClick={() => navigate('/analysis')}
                className={getActiveClass('/analysis')}
                style={{ 
                  borderBottom: window.location.pathname === '/analysis' ? '3px solid #00A651' : 'none',
                  fontWeight: window.location.pathname === '/analysis' ? '600' : '400',
                  color: window.location.pathname === '/analysis' ? '#00A651' : '#495057'
                }}
              >
                <i className="bi bi-graph-up me-2"></i>
                Data Analysis
              </Nav.Link>
            </Nav.Item>
          )}
          <Nav.Item>
            <Nav.Link 
              onClick={() => navigate('/questionnaire')}
              className={getActiveClass('/questionnaire')}
              style={{ 
                borderBottom: window.location.pathname === '/questionnaire' ? '3px solid #00A651' : 'none',
                fontWeight: window.location.pathname === '/questionnaire' ? '600' : '400',
                color: window.location.pathname === '/questionnaire' ? '#00A651' : '#495057'
              }}
            >
              <i className="bi bi-file-text me-2"></i>
              Questionnaire
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link 
              onClick={() => navigate('/reporting')}
              className={getActiveClass('/reporting')}
              style={{ 
                borderBottom: window.location.pathname === '/reporting' ? '3px solid #00A651' : 'none',
                fontWeight: window.location.pathname === '/reporting' ? '600' : '400',
                color: window.location.pathname === '/reporting' ? '#00A651' : '#495057'
              }}
            >
              <i className="bi bi-file-earmark-bar-graph me-2"></i>
              Reporting
            </Nav.Link>
          </Nav.Item>
          {SHOW_DATA_AUGMENTATION && (
            <Nav.Item>
              <Nav.Link 
                onClick={() => navigate('/data-augmentation')}
                className={getActiveClass('/data-augmentation')}
                style={{ 
                  borderBottom: window.location.pathname === '/data-augmentation' ? '3px solid #00A651' : 'none',
                  fontWeight: window.location.pathname === '/data-augmentation' ? '600' : '400',
                  color: window.location.pathname === '/data-augmentation' ? '#00A651' : '#495057'
                }}
              >
                <i className="bi bi-database-add me-2"></i>
                LTI Data Collection
              </Nav.Link>
            </Nav.Item>
          )}
        </Nav>
      </Card.Body>
    </Card>
  );
};

// Main App Content component
const AppContent = () => {
  const { isAuthenticated, loading, error } = useAuth();
  const [orgId, setOrgId] = useState(() => {
    const storedOrgId = localStorage.getItem('orgId');
    // Default to '169' if null or invalid
    return storedOrgId && storedOrgId !== 'null' && storedOrgId !== 'undefined' ? storedOrgId : '169';
  });
  const [inputOrgId, setInputOrgId] = useState(() => {
    const storedOrgId = localStorage.getItem('orgId');
    // Default to '169' if null or invalid
    return storedOrgId && storedOrgId !== 'null' && storedOrgId !== 'undefined' ? storedOrgId : '169';
  });
  const [showOrgPanel, setShowOrgPanel] = useState(true);
  const [showOrgHistory, setShowOrgHistory] = useState(false);
  const [orgIdHistory, setOrgIdHistory] = useState([]);

  // Listen for org ID changes
  useEffect(() => {
    const handleOrgIdChange = (event) => {
      const detail = event.detail;
      const newOrgId = typeof detail === 'object' ? (detail.orgId) : detail;
      // Validate and sanitize orgId
      const validOrgId = newOrgId && newOrgId !== 'null' && newOrgId !== 'undefined' ? newOrgId : '169';
      setOrgId(validOrgId);
      setInputOrgId(validOrgId);
    };

    window.addEventListener(ORG_ID_CHANGE_EVENT, handleOrgIdChange);
    
    // Also check localStorage on mount in case it was set during login
    const storedOrgId = localStorage.getItem('orgId');
    if (storedOrgId && storedOrgId !== 'null' && storedOrgId !== 'undefined') {
      if (storedOrgId !== orgId) {
        setOrgId(storedOrgId);
        setInputOrgId(storedOrgId);
      }
    } else if (!orgId || orgId === 'null' || orgId === 'undefined') {
      // Set default if not already set
      setOrgId('169');
      setInputOrgId('169');
    }
    
    return () => {
      window.removeEventListener(ORG_ID_CHANGE_EVENT, handleOrgIdChange);
    };
  }, [orgId]);

  // Load org history when component mounts
  useEffect(() => {
    try {
      const history = JSON.parse(localStorage.getItem('orgIdHistory') || '[]');
      setOrgIdHistory(history);
    } catch (e) {
      console.error('Error loading orgId history:', e);
    }
  }, []);

  // Update history when org ID changes
  useEffect(() => {
    try {
      const history = JSON.parse(localStorage.getItem('orgIdHistory') || '[]');
      setOrgIdHistory(history);
    } catch (e) {
      console.error('Error loading orgId history:', e);
    }
  }, [orgId]);


  const handleOrgIdUpdate = async () => {
    try {
      const newOrgId = inputOrgId.trim();
      if (!newOrgId) {
        throw new Error('Organization ID cannot be empty');
      }
      
      setOrgId(newOrgId);
      await updateOrganization(newOrgId);
      clearCache();
      
      const previousOrgId = localStorage.getItem('orgId');
      const timestamp = new Date().toISOString();
      
      // Update history
      const history = JSON.parse(localStorage.getItem('orgIdHistory') || '[]');
      history.unshift({
        timestamp,
        previousOrgId,
        newOrgId,
        source: 'manual_update'
      });
      if (history.length > 10) history.length = 10;
      localStorage.setItem('orgIdHistory', JSON.stringify(history));
      setOrgIdHistory(history);
      
      localStorage.setItem('orgId', newOrgId);
      window.dispatchEvent(new CustomEvent(ORG_ID_CHANGE_EVENT, { 
        detail: { orgId: newOrgId, timestamp, previousOrgId }
      }));
    } catch (err) {
      console.error('Error updating organization:', err);
      throw err;
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <Container>
        <Navigation />
        
        {isAuthenticated && (
          <Row className="mb-4">
            <Col>
              <Card className="shadow-sm">
                <Card.Header 
                  className="d-flex justify-content-between align-items-center bg-white flex-wrap gap-2"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setShowOrgPanel(!showOrgPanel)}
                >
                  <div className="d-flex align-items-center">
                    <i className="bi bi-building me-2 text-primary"></i>
                    <h6 className="mb-0">Organization Settings</h6>
                  </div>
                  <div className="d-flex align-items-center flex-shrink-0">
                    <span className="small text-muted me-2">
                      {showOrgPanel ? 'Hide' : 'Show'} Settings
                    </span>
                    <i className={`bi bi-chevron-${showOrgPanel ? 'up' : 'down'}`}></i>
                  </div>
                </Card.Header>
                {showOrgPanel && (
                  <Card.Body>
                    <Row className="g-3">
                      <Col md={6}>
                        <label className="form-label small text-muted">Organization ID</label>
                        <InputGroup>
                          <InputGroup.Text>
                            <i className="bi bi-hash"></i>
                          </InputGroup.Text>
                          <input
                            type="text"
                            value={inputOrgId}
                            onChange={(e) => setInputOrgId(e.target.value)}
                            className="form-control"
                            placeholder="Enter Org ID"
                          />
                          <button
                            className="btn btn-success"
                            onClick={handleOrgIdUpdate}
                          >
                            <i className="bi bi-check-lg me-1"></i>
                            Update
                          </button>
                        </InputGroup>
                      </Col>
                      <Col md={6}>
                        <label className="form-label small text-muted">Quick Actions</label>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-outline-info btn-sm"
                            onClick={() => setShowOrgHistory(!showOrgHistory)}
                          >
                            <i className="bi bi-clock-history me-1"></i>
                            History
                          </button>
                          <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => clearCache()}
                          >
                            <i className="bi bi-arrow-clockwise me-1"></i>
                            Refresh
                          </button>
                        </div>
                      </Col>
                    </Row>
                    {showOrgHistory && orgIdHistory.length > 0 && (
                      <div className="mt-3 pt-3 border-top">
                        <h6 className="small text-muted mb-2">
                          <i className="bi bi-clock-history me-1"></i>
                          Organization ID History
                        </h6>
                        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                          <table className="table table-sm table-hover">
                            <thead className="table-light">
                              <tr>
                                <th className="small">Time</th>
                                <th className="small">From</th>
                                <th className="small">To</th>
                                <th className="small">Source</th>
                              </tr>
                            </thead>
                            <tbody>
                              {orgIdHistory.map((entry, index) => (
                                <tr key={index}>
                                  <td className="small">{new Date(entry.timestamp).toLocaleString()}</td>
                                  <td><Badge bg="secondary">{entry.previousOrgId || '-'}</Badge></td>
                                  <td><Badge bg="success">{entry.newOrgId}</Badge></td>
                                  <td className="small text-muted">{entry.source}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </Card.Body>
                )}
              </Card>
            </Col>
          </Row>
        )}

        <Routes>
          <Route path="/auth" element={<Authentication />} />
          {SHOW_DATA_VIEW && (
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <DataView orgId={orgId} />
                </ProtectedRoute>
              }
            />
          )}
          {SHOW_DATA_ANALYSIS && (
            <Route
              path="/analysis"
              element={
                <ProtectedRoute>
                  <DataAnalysis orgId={orgId} />
                </ProtectedRoute>
              }
            />
          )}
          <Route
            path="/questionnaire"
            element={
              <ProtectedRoute>
                <Questionnaire orgId={orgId} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reporting"
            element={
              <ProtectedRoute>
                <Reporting orgId={orgId} />
              </ProtectedRoute>
            }
          />
          {SHOW_DATA_AUGMENTATION && (
            <Route
              path="/data-augmentation"
              element={
                <ProtectedRoute>
                  <DataAugmentation orgId={orgId} />
                </ProtectedRoute>
              }
            />
          )}
        </Routes>

        {error && (
          <div className="alert alert-danger mt-3">
            {error}
          </div>
        )}
      </Container>
    </div>
  );
};

// Main App component
const App = () => {
  const isInIframe = window.self !== window.top;
  if (config.isEmbedded && !isInIframe) {
    window.location.href = 'https://global.remchannel.com'; 
    return null;
  }

  // If embedded mode is enabled, render the EmbeddedApp component
  if (config.isEmbedded) {
    return (
      <Router>
        <AuthProvider>
          <EmbeddedApp />
        </AuthProvider>
      </Router>
    );
  }

  // Otherwise, render the regular app
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
};

export default App; 